import {keyframes} from "@emotion/react";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import {Box, CircularProgress, IconButton, TextField, Tooltip} from "@mui/material";
import {alpha, styled} from "@mui/material/styles";
import ReactMarkdown from "react-markdown";
import {useTranslation} from "react-i18next";
import {MessageRole} from "../../util/role";
import {Message} from "../../util/types";

type Props = {
    messages: Message[];
    editingMessageIndex: number | null;
    editingValue: string;
    isEditButtonDisabled: boolean;
    isEditPending: boolean;
    onEditCancel: () => void;
    onEditChange: (value: string) => void;
    onEditSave: () => void;
    onEditStart: (message: Message) => void;
};

const slideInRight = keyframes`
    from {
        opacity: 0;
        transform: translateX(20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const slideInLeft = keyframes`
    from {
        opacity: 0;
        transform: translateX(-20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const MessageBubble = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isUserMessage",
})<{isUserMessage: boolean}>(({theme, isUserMessage}) => ({
    padding: theme.spacing(1.5, 2),
    marginBottom: 0,
    borderRadius: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: 1.6,
    fontSize: 15,
    ...(isUserMessage
        ? {
              borderBottomRightRadius: 4,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.dark, 0.24),
              boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.18)}`,
              animation: `${slideInRight} 0.3s ease-out`,
          }
        : {
              borderBottomLeftRadius: 4,
              backgroundColor:
                  theme.palette.mode === "light"
                      ? alpha(theme.palette.primary.main, 0.07)
                      : alpha(theme.palette.primary.light, 0.14),
              color: theme.palette.text.primary,
              border: "1px solid",
              borderColor:
                  theme.palette.mode === "light"
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.primary.light, 0.16),
              boxShadow: `0 10px 24px ${alpha(theme.palette.common.black, 0.08)}`,
              animation: `${slideInLeft} 0.3s ease-out`,
          }),
}));

const MessageList: React.FC<Props> = ({
    messages,
    editingMessageIndex,
    editingValue,
    isEditButtonDisabled,
    isEditPending,
    onEditCancel,
    onEditChange,
    onEditSave,
    onEditStart,
}) => {
    const {t: translate} = useTranslation();

    const handleCopyToClipboard = (content: string) => {
        navigator.clipboard.writeText(content);
    };

    const handleEditKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!(event.target instanceof HTMLTextAreaElement)) {
            return;
        }

        if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
            event.preventDefault();
            onEditSave();
        }
    };

    const getEditFieldWidth = (content: string) => {
        const lineLengths = content.split("\n").map((line) => line.length);
        const longestLineLength = Math.max(...lineLengths, 0);
        const clampedWidth = Math.min(72, Math.max(28, longestLineLength + 4));

        return `${clampedWidth}ch`;
    };

    return (
        <>
            {messages.map((chatItem) => {
                const key = `${chatItem.role}-${chatItem.chatIndex}-${chatItem.timestamp}`;
                const isUserMessage = chatItem.role === MessageRole.USER;
                const isEditing = editingMessageIndex === chatItem.chatIndex;
                const justifyContent = isUserMessage ? "flex-end" : "flex-start";

                return (
                    <Box component="li" key={key} sx={{listStyle: "none"}}>
                        <Box sx={{mt: 2, width: "100%", display: "flex", justifyContent}}>
                            <Box sx={{maxWidth: "70%"}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                    {isEditing ? (
                                        <TextField
                                            autoFocus
                                            multiline
                                            minRows={2}
                                            maxRows={8}
                                            value={editingValue}
                                            disabled={isEditPending}
                                            onChange={(event) => onEditChange(event.target.value)}
                                            onKeyDown={handleEditKeyDown}
                                            placeholder={translate("chat.editPlaceholder")}
                                            sx={{
                                                width: getEditFieldWidth(editingValue),
                                                maxWidth: "100%",
                                            }}
                                        />
                                    ) : (
                                        <MessageBubble isUserMessage={isUserMessage}>
                                            {isUserMessage ? (
                                                chatItem.content
                                            ) : (
                                                <Box sx={{whiteSpace: "normal"}}>
                                                    <ReactMarkdown>{chatItem.content}</ReactMarkdown>
                                                </Box>
                                            )}
                                        </MessageBubble>
                                    )}
                                </Box>
                                <Box sx={{display: "flex", justifyContent: "flex-end", gap: 0.5}}>
                                    {isUserMessage ? (
                                        isEditing ? (
                                            <>
                                                {isEditPending ? (
                                                    <CircularProgress
                                                        size={16}
                                                        sx={{
                                                            mt: 0.5,
                                                        }}
                                                    />
                                                ) : null}
                                                <Tooltip title={translate("chat.saveEdit")}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={onEditSave}
                                                            disabled={isEditPending}
                                                            aria-label={translate("chat.saveEdit")}
                                                            sx={(theme) => ({
                                                                color: alpha(theme.palette.primary.main, 0.9),
                                                            })}
                                                        >
                                                            <CheckIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={translate("chat.cancelEdit")}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={onEditCancel}
                                                            disabled={isEditPending}
                                                            aria-label={translate("chat.cancelEdit")}
                                                            sx={(theme) => ({
                                                                color: alpha(theme.palette.primary.main, 0.9),
                                                            })}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            <Tooltip title={translate("chat.edit")}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onEditStart(chatItem)}
                                                    disabled={isEditButtonDisabled}
                                                    aria-label={translate("chat.edit")}
                                                    sx={(theme) => ({
                                                        color: alpha(theme.palette.primary.main, 0.9),
                                                    })}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )
                                    ) : null}
                                    {isEditing ? null : (
                                        <Tooltip title={translate("chat.copyToClipboard")}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleCopyToClipboard(chatItem.content)}
                                                aria-label={translate("chat.copyToClipboard")}
                                                sx={(theme) => ({
                                                    color: isUserMessage
                                                        ? alpha(theme.palette.primary.main, 0.9)
                                                        : theme.palette.text.secondary,
                                                })}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </>
    );
};

export default MessageList;
